/**
 * Accordion
 * アコーディオン開閉機能
 *
 * @usage
 * HTML: 
 * <div class="js-ac">
 *   <button class="js-ac-trigger">開く/閉じる</button>
 *   <div class="js-ac-content">コンテンツ</div>
 * </div>
 * JS: new Accordion();
 */
export default class Accordion {
  constructor(options = {}) {
    this.options = {
      selector: '.js-ac',
      triggerSelector: '.js-ac-trigger',
      contentSelector: '.js-ac-content',
      targetSelector: '.js-ac-target', // 高さ計測用のセレクタ
      activeClass: 'is-active',
      duration: 300,
      closeOthers: false, // 他のアコーディオンを閉じるか
      setHeightVariable: true, // CSS変数として高さを設定するか
      ...options
    };

    this.accordions = document.querySelectorAll(this.options.selector);
    this.instanceId = Date.now(); // インスタンス識別用ID
    this.resizeTimer = null;
    
    if (this.accordions.length > 0) {
      this.init();
      this.setupResizeListener();
    }
  }

  init() {
    this.accordions.forEach((accordion, index) => {
      const trigger = accordion.querySelector(this.options.triggerSelector);
      const content = accordion.querySelector(this.options.contentSelector);
      
      if (trigger) {
        // data-ac-index属性がある場合のみ高さ計算を行う
        const hasAcIndex = accordion.hasAttribute('data-ac-index');
        
        // 高さをCSS変数として設定（data-ac-index属性がある場合のみ）
        if (this.options.setHeightVariable && hasAcIndex) {
          this.setHeightVariable(accordion);
        }
        
        // ARIA属性の設定
        this.setupAccessibility(accordion, trigger, content, index);
        
        // イベントリスナーの設定
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggle(accordion);
        });

        // キーボード操作対応
        trigger.addEventListener('keydown', (e) => {
          // Enterキーまたはスペースキーで開閉
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggle(accordion);
          }
        });
      }
    });
  }
  
  setupResizeListener() {
    if (typeof window === 'undefined' || !this.options.setHeightVariable) {
      return;
    }
    
    // 単一のリサイズリスナーで全アコーディオンの高さを更新
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.updateHeights();
        console.log('Accordion heights updated on resize');
      }, 250);
    });
  }

  setHeightVariable(accordion) {
    // data-ac-index属性をチェック
    if (!accordion.hasAttribute('data-ac-index')) {
      return; // data-ac-index属性がない場合は処理しない
    }
    
    const target = accordion.querySelector(this.options.targetSelector);
    
    if (target) {
      // targetの最初の子要素を取得
      const firstChild = target.firstElementChild;
      
      if (firstChild) {
        // firstChildの実際の高さを取得（内容全体の高さ）
        const rect = firstChild.getBoundingClientRect();
        const totalHeight = rect.height;
        
        // data-ac-indexの値を使用してCSS変数名を生成
        const index = accordion.dataset.acIndex;
        const cssVariableName = `--ac-height-${index}`;
        
        // CSS変数として設定
        accordion.style.setProperty(cssVariableName, `${totalHeight}px`);
        
        // デバッグ用のdata属性も設定
        accordion.dataset.accordionHeight = Math.ceil(totalHeight);
        
        // デバッグ用のログ
        console.log(`Accordion [data-ac-index="${index}"]:`, {
          firstChild: firstChild.className,
          childrenCount: firstChild.children.length,
          calculatedHeight: totalHeight,
          cssVariable: cssVariableName
        });
      }
    }
  }

  setupAccessibility(accordion, trigger, content, index) {
    // トリガーにARIA属性を設定
    const triggerId = trigger.id || `accordion-trigger-${index}`;
    const contentId = content?.id || `accordion-content-${index}`;
    
    if (!trigger.id) trigger.id = triggerId;
    if (content && !content.id) content.id = contentId;
    
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', contentId);
    
    if (content) {
      content.setAttribute('aria-hidden', 'true');
      content.setAttribute('aria-labelledby', triggerId);
    }

    // ボタンでない場合はroleを追加
    if (trigger.tagName !== 'BUTTON') {
      trigger.setAttribute('role', 'button');
      trigger.setAttribute('tabindex', '0');
    }
  }

  toggle(accordion) {
    const isActive = accordion.classList.contains(this.options.activeClass);
    
    // 他のアコーディオンを閉じる設定の場合
    if (this.options.closeOthers && !isActive) {
      this.closeAll();
    }
    
    if (isActive) {
      this.close(accordion);
    } else {
      this.open(accordion);
    }
  }

  open(accordion) {
    const trigger = accordion.querySelector(this.options.triggerSelector);
    const content = accordion.querySelector(this.options.contentSelector);
    
    // クラスを追加
    accordion.classList.add(this.options.activeClass);
    
    // 高さを再計算（開いた時に高さが変わる可能性があるため）
    if (this.options.setHeightVariable && accordion.hasAttribute('data-ac-index')) {
      // 少し遅延させて、DOMの更新後に高さを取得
      setTimeout(() => {
        this.setHeightVariable(accordion);
      }, 10);
    }
    
    // ARIA属性を更新
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    }
    if (content) {
      content.setAttribute('aria-hidden', 'false');
    }
    
    // カスタムイベントを発火
    accordion.dispatchEvent(new CustomEvent('accordion:opened', {
      detail: { accordion, trigger, content }
    }));
  }

  close(accordion) {
    const trigger = accordion.querySelector(this.options.triggerSelector);
    const content = accordion.querySelector(this.options.contentSelector);
    
    // クラスを削除
    accordion.classList.remove(this.options.activeClass);
    
    // ARIA属性を更新
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }
    if (content) {
      content.setAttribute('aria-hidden', 'true');
    }
    
    // カスタムイベントを発火
    accordion.dispatchEvent(new CustomEvent('accordion:closed', {
      detail: { accordion, trigger, content }
    }));
  }

  openAll() {
    this.accordions.forEach(accordion => {
      this.open(accordion);
    });
  }

  closeAll() {
    this.accordions.forEach(accordion => {
      this.close(accordion);
    });
  }

  // 特定のアコーディオンを開く/閉じる（外部から制御用）
  openByIndex(index) {
    const accordion = this.accordions[index];
    if (accordion) {
      this.open(accordion);
    }
  }

  closeByIndex(index) {
    const accordion = this.accordions[index];
    if (accordion) {
      this.close(accordion);
    }
  }

  toggleByIndex(index) {
    const accordion = this.accordions[index];
    if (accordion) {
      this.toggle(accordion);
    }
  }

  // 指定された要素を含むアコーディオンを取得
  getAccordionByElement(element) {
    return element.closest(this.options.selector);
  }

  // 全てのアコーディオンの高さを再計算
  updateHeights() {
    if (this.options.setHeightVariable) {
      this.accordions.forEach(accordion => {
        if (accordion.hasAttribute('data-ac-index')) {
          this.setHeightVariable(accordion);
        }
      });
    }
  }

  // 特定のアコーディオンの高さを再計算
  updateHeightByIndex(index) {
    const accordion = this.accordions[index];
    if (accordion && this.options.setHeightVariable && accordion.hasAttribute('data-ac-index')) {
      this.setHeightVariable(accordion);
    }
  }

  destroy() {
    // リサイズタイマーをクリア
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    
    this.accordions.forEach((accordion) => {
      const trigger = accordion.querySelector(this.options.triggerSelector);
      
      // クラスを削除
      accordion.classList.remove(this.options.activeClass);
      
      // CSS変数を削除
      if (accordion.hasAttribute('data-ac-index')) {
        const index = accordion.dataset.acIndex;
        accordion.style.removeProperty(`--ac-height-${index}`);
        delete accordion.dataset.accordionHeight;
      }
      
      // ARIA属性を削除
      if (trigger) {
        trigger.removeAttribute('aria-expanded');
        trigger.removeAttribute('aria-controls');
        if (trigger.tagName !== 'BUTTON') {
          trigger.removeAttribute('role');
          trigger.removeAttribute('tabindex');
        }
      }
      
      const content = accordion.querySelector(this.options.contentSelector);
      if (content) {
        content.removeAttribute('aria-hidden');
        content.removeAttribute('aria-labelledby');
      }
    });
  }
}